<template>
  <section v-if="publications?.length" class="publications pe-section">
    <h3>
      Publications
      <SectionActions
        v-show="isPowerEdit"
        class="actions"
        section="publications"
      />
    </h3>
    <section class="list">
      <template v-for="(pub, index) in publications" :key="pub.name">
        <section v-if="pub.name || pub.summary" class="publication">
          <ItemActions
            v-show="isPowerEdit"
            class="itemActions"
            :index="index"
            section="publications"
            :uuid="pub.uuid"
            :actions="['edit', 'delete']"
          />
          <div class="details">
            <a class="url" :href="getNewTabUrl(pub.url)" target="_blank">
              <div
                @click.prevent
                class="title"
                v-editable="isPowerEdit"
                :data-target-path="`resume.publications.${index}.name`"
                v-text="pub.name"
              ></div>
            </a>
            <div v-if="pub.releaseDate">
              <DateDisplay
                class="date"
                :date="pub.releaseDate"
                :updatePath="`resume.publications.${index}.releaseDate`"
                :isEditable="isPowerEdit"
              />
            </div>
          </div>
          <div class="publisher">
            <span
              v-editable="isPowerEdit"
              :data-target-path="`resume.publications.${index}.publisher`"
              v-text="pub.publisher"
            ></span>
          </div>
          <div
            class="summary publication-text"
            v-word-count
            v-editable="isPowerEdit"
            :data-target-path="`resume.publications.${index}.summary`"
            v-text="pub.summary"
            @click="
              evt =>
                openRephrasePopup(
                  `resume.publications.${index}.summary`,
                  evt.target
                )
            "
          ></div>
        </section>
      </template>
    </section>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { Resume } from '@/types/resume/v1'
import DateDisplay from './support/DateDisplay.vue'
import { useStructuredResumeStore } from '@/store/pinia/structuredResume'
import SectionActions from './SectionActions.vue'
import ItemActions from './ItemActions.vue'
import { usePowerEditStore } from '@/store/pinia/powerEdit'
import { getNewTabUrl } from '@/lib/urls'

export default defineComponent({
  name: 'Publications',
  components: {
    DateDisplay,
    SectionActions,
    ItemActions,
  },
  props: {
    publications: {
      type: Object as PropType<Resume.Publication[]>,
      required: false,
    },
  },
  setup() {
    const store = useStructuredResumeStore()
    const powerEditStore = usePowerEditStore()
    const { closePopup, openRephrasePopup } = powerEditStore

    return {
      isPowerEdit: store.isPowerEdit,
      closePopup,
      openRephrasePopup,
      getNewTabUrl,
    }
  },
})
</script>

<style lang="postcss" scoped>
.publications {
  @apply text-base-em leading-[inherit] pt-6-em;
  h3 {
    @apply leading-none text-xl-em flex items-center;
    .actions {
      @apply hidden;
    }
    &:hover {
      .actions {
        @apply flex;
      }
    }
  }
  .list {
    .publication {
      @apply pt-4-em leading-[inherit] relative;
      .itemActions {
        @apply hidden;
      }
      &:hover {
        .itemActions {
          @apply flex;
        }
      }
      .details {
        @apply flex flex-row justify-between items-start flex-nowrap space-x-2-em;
        .url {
          @apply text-inherit cursor-text;
        }
        .title {
          @apply font-semibold;
        }
        .date {
          @apply text-sm-em whitespace-nowrap;
        }
      }
      .publisher {
        @apply italic mb-2-em flex flex-row justify-between items-center flex-wrap text-sm-em leading-normal;
      }
      .summary {
        @apply text-sm-em whitespace-pre-wrap leading-[inherit];
      }
    }
  }
}
</style>
